// @flow

import http from "@http";
import config from "@config";

import { all, put, takeEvery, call } from "redux-saga/effects";
import type { Action } from "redux-action";

import actionCreators from "@modules/service/actionCreator";
import {
  GetUserAccounts,
  ExportUserAccounts,
  ExportUserAccountsSuccess,
  GetUserTypes,
} from "@modules/service/constants/actions";
import { sortToString, transformSort } from "@transformers/sort";
import { transformFilters } from "@transformers/request";
import { downloadCsvFile } from "@sagas/utils";

type UserAccountsRequest = $Exact<{
  clientId: string,
  page?: number,
  sort?: Object,
  filters?: Object,
}>;

export function* onGetUserAccounts({
  payload,
}: Action<UserAccountsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/system-management/client/${payload.clientId}/user-list`,
      {
        params: {
          search: transformFilters(payload.filters || {}),
          limit: config.results50PerPage,
          page: payload.page ? payload.page : 1,
          sort: payload.sort ? sortToString(transformSort(payload.sort)) : "last_name,asc",
        },
      },
    );

    yield put(actionCreators.GetUserAccountsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetUserAccountsError(e.response));
  }
}

export function* onExportUserAccounts({
  payload,
}: Action<UserAccountsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/system-management/client/${payload.clientId}/user-list/export`,
      {
        headers: { Accept: "text/csv" },
        params: {
          search: transformFilters(payload.filters || {}),
        },
      },
    );

    yield put(actionCreators.ExportUserAccountsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.ExportUserAccountsError(e.response));
  }
}

export function* onExportUserAccountsSuccess({ payload }: Action<any>): Generator<*, void, any> {
  yield downloadCsvFile(payload, "user-accounts-export.csv");
}

export function* onGetUserTypes({ payload }: Action<{ clientId: string }>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(http.get, `api/v1/client/${payload.clientId}/data/user-type`);

    yield put(actionCreators.GetUserTypesSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetUserTypesError(e.response));
  }
}

export function* SystemAccessSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetUserAccounts, onGetUserAccounts),
    takeEvery(ExportUserAccounts, onExportUserAccounts),
    takeEvery(ExportUserAccountsSuccess, onExportUserAccountsSuccess),
    takeEvery(GetUserTypes, onGetUserTypes),
  ]);
}

export default SystemAccessSagas;
